import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

// Module-level cache for /api/betslip/build responses. The BetslipModal
// remounts every time the panel is toggled open, so a component ref would
// reset and force a fresh POST each open. This cache survives remounts:
// same legs + region within CACHE_TTL_MS reuses the previous response
// instead of re-hitting the backend. Add/remove/region changes shift the
// key and skip the cache naturally.
const CACHE_TTL_MS = 60 * 1000; // 1 minute
const buildCache = { key: null, at: 0, data: null };
const buildCacheKey = (legs, region) => JSON.stringify({ legs, region });

const BetslipModal = ({ show, onHide, betslipItems, onRemoveBet, onClearAll, isDarkMode, getTeamLogoUrl, renderInline, showBetslipIframe, onToggleBetFinder, onPlayerClick, selectedSport }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // { type: 'success'/'error', message: '...' }
  const [buildResponse, setBuildResponse] = useState(null);
  const [buildLoading, setBuildLoading] = useState(false);
  const [buildError, setBuildError] = useState(null);
  const buildAbortRef = useRef(null);
  const [buildTrigger, setBuildTrigger] = useState(0);
  const [copiedBookId, setCopiedBookId] = useState(null);
  const [savingLink, setSavingLink] = useState(false);
  const [savedLink, setSavedLink] = useState(false);
  const [activeTab, setActiveTab] = useState('current');
  const [mySlips, setMySlips] = useState([]);
  const [mySlipsLoading, setMySlipsLoading] = useState(false);
  const [mySlipsError, setMySlipsError] = useState(null);
  const [expandedSlipId, setExpandedSlipId] = useState(null);
  const [region, setRegion] = useState('us/nj');

  // POST to /api/betslip/build whenever betslipItems changes
  useEffect(() => {
    if (betslipItems.length === 0) {
      setBuildResponse(null);
      setBuildError(null);
      return;
    }

    const betslipArray = betslipItems.map(bet => {
      let value = bet.line !== null && bet.line !== undefined ? parseFloat(bet.line) : null;
      return {
        gameId: bet.gameId,
        playerId: bet.playerId,
        name: !bet.playerId ? (bet.name || null) : undefined,
        team: bet.team || undefined,
        teamId: (bet.market === 'spreads' || bet.market === 'h2h') ? (bet.teamId || undefined) : undefined,
        game: bet.awayTeam && bet.homeTeam ? `${bet.awayTeam} @ ${bet.homeTeam}` : undefined,
        value,
        market: bet.market || null,
        method: bet.side || null
      };
    });

    // Rate-limit: reuse the cached response if the legs + region are
    // unchanged AND the last successful fetch is < CACHE_TTL_MS old.
    // buildTrigger is a manual refresh escape hatch (its value is part of
    // the effect deps but not the cache key — bumping it always re-fetches).
    const key = buildCacheKey(betslipArray, region);
    if (
      buildCache.key === key &&
      buildCache.data &&
      Date.now() - buildCache.at < CACHE_TTL_MS &&
      buildTrigger === (buildCache.trigger ?? buildTrigger)
    ) {
      setBuildResponse(buildCache.data);
      setBuildError(null);
      setBuildLoading(false);
      return;
    }

    // Abort previous request
    if (buildAbortRef.current) {
      buildAbortRef.current.abort();
    }
    const controller = new AbortController();
    buildAbortRef.current = controller;

    const postBuild = async () => {
      setBuildLoading(true);
      setBuildError(null);
      try {
        const response = await fetch('/api/betslip/build', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ legs: betslipArray, region }),
          signal: controller.signal
        });
        const data = await response.json();
        if (!response.ok) {
          setBuildError(data.error || `HTTP ${response.status}`);
          setBuildResponse(null);
          // Don't cache errors — next open should retry immediately.
        } else {
          setBuildResponse(data);
          buildCache.key = key;
          buildCache.at = Date.now();
          buildCache.data = data;
          buildCache.trigger = buildTrigger;
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          setBuildError(err.message);
          setBuildResponse(null);
        }
      } finally {
        setBuildLoading(false);
      }
    };

    postBuild();

    return () => {
      controller.abort();
    };
  }, [betslipItems, buildTrigger, region]);

  // Reset to current tab when modal closes
  useEffect(() => {
    if (!show && !renderInline) {
      setActiveTab('current');
    }
  }, [show, renderInline]);

  const fetchMySlips = async () => {
    setMySlipsLoading(true);
    setMySlipsError(null);
    try {
      const response = await fetch('/api/betslip/my-slips', {
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        setMySlipsError(data.error || `HTTP ${response.status}`);
      } else {
        setMySlips(data.betslips || []);
      }
    } catch (err) {
      setMySlipsError(err.message);
    } finally {
      setMySlipsLoading(false);
    }
  };

  // Convert betslip items to simplified format for API
  const formatBetslipForAPI = (items) => {
    return items.map(bet => {
      const marketName = formatMarketName(bet.market);

      // Generate display text (user-friendly)
      let displayText = '';
      if (bet.side === 'over' && bet.line) {
        displayText = bet.playerName ? `${bet.playerName} Over ${bet.line} ${marketName.toLowerCase()}` : `Over ${bet.line} ${marketName.toLowerCase()}`;
      } else if (bet.side === 'under' && bet.line) {
        displayText = bet.playerName ? `${bet.playerName} Under ${bet.line} ${marketName.toLowerCase()}` : `Under ${bet.line} ${marketName.toLowerCase()}`;
      } else if (bet.side === 'yes' && bet.market === 'player_anytime_td' && bet.line) {
        displayText = `${bet.playerName} over ${bet.line} ${marketName.toLowerCase()}`;
      } else if (bet.side === 'yes' && bet.market === 'player_anytime_td') {
        displayText = `${bet.playerName} ${marketName.toLowerCase()} scorer`;
      } else if (bet.market === 'player_first_basket') {
        displayText = `${bet.playerName} first basket scorer`;
      } else if (bet.side === 'moneyline') {
        displayText = `${bet.playerName} ${marketName}`;
      } else if (bet.side === 'spread' && bet.line) {
        displayText = `${bet.playerName} ${bet.line > 0 ? '+' : ''}${bet.line} ${marketName.toLowerCase()}`;
      } else if (bet.market === 'spreads' && bet.line != null) {
        displayText = `${bet.playerName} ${bet.line > 0 ? '+' : ''}${bet.line} ${marketName.toLowerCase()}`;
      } else if (bet.market === 'h2h' && (bet.side === 'away' || bet.side === 'home')) {
        displayText = `${bet.playerName} ${marketName.toLowerCase()}`;
      } else {
        displayText = `${bet.playerName} ${marketName.toLowerCase()}`;
      }

      // Return simplified bet object
      return {
        display: displayText,
        player: bet.playerName,
        market: bet.market,
        line: bet.line || null,
        side: bet.side
      };
    });
  };

  // Handle betslip submission
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formattedBetslip = formatBetslipForAPI(betslipItems);
      console.log('Submitting betslip:', formattedBetslip);

      const response = await fetch('/api/betslip', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          betslip: formattedBetslip
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Handle different response types
        if (data.duplicate) {
          setSubmitStatus({ type: 'warning', message: data.message || 'You already have this betslip saved' });
        } else if (data.shared) {
          setSubmitStatus({ type: 'info', message: data.message || 'Added to existing betslip shared by other users' });
        } else if (data.new) {
          setSubmitStatus({ type: 'success', message: 'Betslip submitted successfully!' });
          // Optionally clear betslip on success
          // setTimeout(() => {
          //   onClearAll();
          //   onHide();
          // }, 2000);
        }
      } else {
        setSubmitStatus({ type: 'error', message: data.error || 'Failed to submit betslip' });
      }
    } catch (error) {
      console.error('Error submitting betslip:', error);
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format market name for display
  const formatMarketName = (market) => {
    const marketNames = {
      // NBA
      'player_points': 'Points',
      'player_rebounds': 'Rebounds',
      'player_assists': 'Assists',
      'player_threes': '3-Pointers',
      'player_first_basket': 'First Basket',
      'player_first_team_basket': 'First Team Basket',
      'player_double_double': 'double double',
      'player_triple_double': 'triple double',
      'player_points_rebounds_assists': 'Pts + Reb + Ast',
      'player_points_rebounds': 'Pts + Reb',
      'player_points_assists': 'Pts + Ast',
      'player_rebounds_assists': 'Reb + Ast',
      'player_steals': 'Steals',
      'player_blocks': 'Blocks',
      'player_turnovers': 'Turnovers',
      // NFL
      'player_pass_yds': 'Pass Yds',
      'player_rush_yds': 'Rush Yds',
      'player_rush_attempts': 'Rush Att',
      'player_reception_yds': 'Rec Yds',
      'player_receptions': 'Receptions',
      'player_rush_reception_yds': 'Ru+Rec Yds',
      'player_field_goals': 'Field Goals',
      'player_anytime_td': 'TD',
      'player_1st_td': 'First TD',
      'player_last_td': 'Last TD',
      'player_tds_over': 'Total TDs',
      'player_pass_tds': 'Pass TDs',
      // MLB
      'player_home_runs': 'Home Runs',
      'player_hits': 'Hits',
      'player_rbis': 'RBIs',
      'player_strikeouts': 'Strikeouts',
      // Game lines
      'h2h': 'Moneyline',
      'spreads': 'Spread',
      'totals': 'Total',
      // Soccer
      'player_goal_scorer_anytime': 'Anytime Goal Scorer',
      'player_first_goal_scorer': 'First Goal Scorer',
      'player_shots': 'Shots',
      'player_shots_on_target': 'Shots on Target',
      'totals_corners': 'Corners',
      'team_totals_corners': 'Team Corners',
      // NHL
      'player_goals': 'Goals',
      'player_shots_on_goal': 'Shots on Goal',
      'player_total_saves': 'Saves'
    };
    return marketNames[market] || market;
  };

  // Binary markets that show as "{Player} - {Market}" with no over/under
  const binaryMarkets = [
    'player_first_basket', 'player_first_team_basket',
    'player_double_double', 'player_triple_double',
    'player_anytime_td', 'player_1st_td', 'player_last_td',
    'player_goal_scorer_anytime', 'player_first_goal_scorer',
    'player_goal_scorer_first', 'player_goal_scorer_last', 'player_last_goal_scorer'
  ];

  // Format bet description
  const formatBetDescription = (bet) => {
    const marketName = formatMarketName(bet.market);

    // Binary markets: "{Player} First Basket"
    if (binaryMarkets.includes(bet.market)) {
      return `${bet.playerName} ${marketName}`;
    }

    // Over/under with line: "{Player} Over 24.5 Points" or "Over 220.5 Totals" for game totals
    if (bet.side === 'over' && bet.line) {
      return bet.playerName ? `${bet.playerName} Over ${bet.line} ${marketName}` : `Over ${bet.line} ${marketName}`;
    }
    if (bet.side === 'under' && bet.line) {
      return bet.playerName ? `${bet.playerName} Under ${bet.line} ${marketName}` : `Under ${bet.line} ${marketName}`;
    }

    // Game lines
    if (bet.side === 'moneyline') {
      return `${bet.playerName} ${marketName}`;
    }
    if (bet.side === 'spread' && bet.line) {
      return `${bet.playerName} ${bet.line > 0 ? '+' : ''}${bet.line} ${marketName}`;
    }
    if (bet.market === 'spreads' && bet.line != null) {
      return `${bet.playerName} ${bet.line > 0 ? '+' : ''}${bet.line} ${marketName}`;
    }
    if (bet.market === 'h2h' && (bet.side === 'away' || bet.side === 'home')) {
      return `${bet.playerName} ${marketName}`;
    }

    // Yes bets with line (TDs over 0.5, etc.)
    if (bet.side === 'yes' && bet.line) {
      return `${bet.playerName} Over ${bet.line} ${marketName}`;
    }

    // Fallback
    return bet.playerName ? `${bet.playerName} ${marketName}` : marketName;
  };

  const formatLegDetails = (bet) => {
    const marketName = formatMarketName(bet.market);
    if (binaryMarkets.includes(bet.market)) return marketName;
    if (bet.side === 'over' && bet.line) return `Over ${bet.line} ${marketName}`;
    if (bet.side === 'under' && bet.line) return `Under ${bet.line} ${marketName}`;
    if (bet.side === 'moneyline') return marketName;
    if (bet.side === 'spread' && bet.line) return `${bet.line > 0 ? '+' : ''}${bet.line} ${marketName}`;
    if (bet.market === 'spreads' && bet.line != null) return `${bet.line > 0 ? '+' : ''}${bet.line} ${marketName}`;
    if (bet.market === 'h2h' && (bet.side === 'away' || bet.side === 'home')) return marketName;
    if (bet.side === 'yes' && bet.line) return `Over ${bet.line} ${marketName}`;
    return marketName;
  };

  // Format bookmaker name for display
  const formatBookmakerName = (bookmaker) => {
    const bookNames = {
      'draftkings': 'DraftKings',
      'fanduel': 'FanDuel',
      'betmgm': 'BetMGM',
      'caesars': 'Caesars',
      'pointsbet': 'PointsBet',
      'betrivers': 'BetRivers',
      'espnbet': 'ESPN BET',
      'fliff': 'Fliff',
      'hardrock': 'Hard Rock',
      'fanatics': 'Fanatics',
      'bet365': 'bet365'
    };
    return bookNames[bookmaker] || bookmaker;
  };

  const renderTabToggle = () => (
    <div style={{ display: 'flex', marginBottom: '0px' }}>
      <button
        onClick={() => setActiveTab('current')}
        style={{
          flex: 1,
          padding: '8px 12px',
          border: 'none',
          borderBottom: 'none',
          borderRadius: '0',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
          backgroundColor: activeTab === 'current' ? 'transparent' : 'rgba(0,0,0,0.3)',
          color: activeTab === 'current' ? '#fff' : '#6c757d',
          transition: 'all 0.15s'
        }}
      >
        Betslip ({betslipItems.length})
      </button>
      <button
        onClick={() => {
          setActiveTab('myslips');
          fetchMySlips();
        }}
        style={{
          flex: 1,
          padding: '8px 12px',
          border: 'none',
          borderBottom: 'none',
          borderRadius: '0',
          fontSize: '0.8rem',
          fontWeight: 600,
          cursor: 'pointer',
          backgroundColor: activeTab === 'myslips' ? 'transparent' : 'rgba(0,0,0,0.3)',
          color: activeTab === 'myslips' ? '#fff' : '#6c757d',
          transition: 'all 0.15s'
        }}
      >
        My Slips
      </button>
    </div>
  );

  const renderMySlips = () => {
    if (mySlipsLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner animation="border" size="sm" variant="info" />
        </div>
      );
    }

    if (mySlipsError) {
      return (
        <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(220,53,69,0.1)', border: '1px solid #dc3545', borderRadius: '6px', fontSize: '0.8rem', color: '#dc3545' }}>
          {mySlipsError}
        </div>
      );
    }

    if (mySlips.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adb5bd' }}>
          <h5 style={{ color: '#adb5bd' }}>No saved betslips</h5>
          <p style={{ fontSize: '0.85rem' }}>Save a betslip to see it here</p>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {mySlips.map(slip => {
          const isExpanded = expandedSlipId === slip._id;
          const legs = slip.payload?.parlays?.parlay?.legs || [];
          const inputLegs = slip.input || [];

          return (
            <div
              key={slip._id}
              style={{
                backgroundColor: '#343a40',
                border: '1px solid #495057',
                borderRadius: '6px',
                padding: '10px',
                cursor: 'pointer'
              }}
              onClick={() => setExpandedSlipId(isExpanded ? null : slip._id)}
            >
              {/* Header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                  {slip.createdAt ? new Date(slip.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + new Date(slip.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                </span>
                <span style={{ fontSize: '0.75rem', color: '#adb5bd' }}>
                  {inputLegs.length} leg{inputLegs.length !== 1 ? 's' : ''}
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/betslip/${slip.betslip_id}`, '_blank');
                  }}
                  style={{ fontSize: '0.75rem', color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Open
                </span>
              </div>

              {/* Legs summary */}
              {inputLegs.map((leg, idx) => {
                const outcomeName = legs[idx]?.outcome_name || '';
                const method = leg.method ? leg.method.charAt(0).toUpperCase() + leg.method.slice(1) : '';
                const value = leg.value != null ? leg.value : '';
                const marketDisplay = formatMarketName(leg.market);
                const legText = outcomeName || `${method} ${value} ${marketDisplay}`.trim();

                return (
                  <div key={idx} style={{ marginBottom: idx < inputLegs.length - 1 ? '4px' : 0 }}>
                    <div style={{ fontSize: '0.8rem', color: '#dee2e6' }}>
                      {legText}
                    </div>
                    {leg.game && (
                      <div style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                        {leg.game}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Expanded: parlay book prices */}
              {isExpanded && (() => {
                const parlayData = slip.payload?.parlays?.parlay?.books || slip.payload?.parlay || slip.payload?.data;
                if (!parlayData || typeof parlayData !== 'object') {
                  return (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #495057', fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
                      No prices available
                    </div>
                  );
                }

                const books = Object.entries(parlayData).map(([name, info]) => ({
                  name,
                  originalId: info.original_id || name,
                  dl: info.dl,
                  price: info.price,
                  error: info.error
                }));

                const withPrice = books.filter(b => b.price).sort((a, b) => {
                  const aNum = parseInt(String(a.price).replace('+', ''));
                  const bNum = parseInt(String(b.price).replace('+', ''));
                  return bNum - aNum;
                });
                const withoutPrice = books.filter(b => !b.price);
                const sorted = [...withPrice, ...withoutPrice];

                if (sorted.length === 0 || withPrice.length === 0) {
                  return (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #495057', fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
                      No prices available
                    </div>
                  );
                }

                return (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #495057' }}>
                    <div style={{ fontSize: '0.75rem', color: '#adb5bd', marginBottom: '6px', fontWeight: 600 }}>Parlay</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {sorted.map(book => {
                        const hasPrice = !!book.price;
                        const isClickable = hasPrice && book.dl;
                        return (
                          <div
                            key={book.originalId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isClickable) window.open(book.dl, '_blank');
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '6px 8px',
                              backgroundColor: hasPrice ? '#1a2a1a' : '#2a2a2a',
                              border: `1px solid ${hasPrice ? '#2d5a2d' : '#444'}`,
                              borderRadius: '6px',
                              cursor: isClickable ? 'pointer' : 'default',
                              opacity: hasPrice ? 1 : 0.4
                            }}
                          >
                            <img
                              src={`/logos/${book.originalId.toLowerCase()}.png`}
                              alt={book.name}
                              style={{ height: '20px', width: '20px', objectFit: 'contain', marginRight: '8px', filter: hasPrice ? 'none' : 'grayscale(100%)' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ flex: 1, fontSize: '0.8rem', color: hasPrice ? '#ccc' : '#666', fontWeight: 500 }}>
                              {formatBookmakerName(book.name)}
                            </span>
                            {hasPrice ? (
                              <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: String(book.price).startsWith('+') ? '#20c997' : '#17a2b8' }}>
                                {book.price}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.7rem', color: '#666' }}>
                                {book.error ? String(book.error).substring(0, 30) : 'N/A'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    );
  };

  // Inline panel rendering (no modal wrapper)
  if (renderInline) {
    return (
      <div>
        {renderTabToggle()}
        {activeTab === 'myslips' ? renderMySlips() : (<>
        {betslipItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#adb5bd' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px', opacity: 0.5 }}>
              <path d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <h5 style={{ color: '#adb5bd' }}>Your betslip is empty</h5>
            <p style={{ fontSize: '0.85rem' }}>Click on odds to add bets</p>
          </div>
        ) : (
          <>
            {(() => {
              // Group bets by gameId
              const gameGroups = [];
              const gameMap = new Map();
              betslipItems.forEach((bet, index) => {
                const key = bet.gameId;
                if (!gameMap.has(key)) {
                  const group = { gameId: key, awayTeam: bet.awayTeam, homeTeam: bet.homeTeam, sport: bet.sport, bets: [] };
                  gameMap.set(key, group);
                  gameGroups.push(group);
                }
                gameMap.get(key).bets.push({ bet, originalIndex: index });
              });

              return gameGroups.map(group => (
                <div
                  key={group.gameId}
                  style={{
                    backgroundColor: '#343a40',
                    border: '1px solid #495057',
                    marginBottom: '6px',
                    borderRadius: '6px',
                    padding: '8px 10px',
                  }}
                >
                  {/* Game matchup header */}
                  <div style={{ fontSize: '0.75rem', color: '#6c757d', marginBottom: '5px' }}>
                    {group.awayTeam} @ {group.homeTeam}
                  </div>
                  {/* Legs for this game */}
                  {group.bets.map(({ bet, originalIndex }, legIdx) => (
                    <div key={bet.id}>
                      {legIdx > 0 && <div style={{ borderTop: '1px solid #495057', margin: '5px 0' }} />}
                      {/* Leg: Player/team name + remove */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                          {getTeamLogoUrl && bet.team && (
                            <img
                              src={getTeamLogoUrl(bet.team, bet.sport || 'nba')}
                              alt={bet.team}
                              style={{ width: '16px', height: '16px', marginRight: '5px', borderRadius: '50%', flexShrink: 0 }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span
                            style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff', cursor: onPlayerClick ? 'pointer' : 'default', textDecoration: onPlayerClick ? 'underline' : 'none', textDecorationColor: '#555', textUnderlineOffset: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            onClick={() => onPlayerClick && onPlayerClick(bet)}
                          >
                            {bet.playerName || (bet.market === 'totals' ? (bet.side === 'over' ? 'Over' : 'Under') : bet.name)}
                          </span>
                        </div>
                        <button
                          onClick={() => onRemoveBet(bet.id)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '0 4px',
                            marginLeft: '4px',
                            flexShrink: 0,
                            lineHeight: 1
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {/* Leg details */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#adb5bd' }}>{formatLegDetails(bet)}</span>
                        {bet.bookmaker && (
                          <span style={{ fontSize: '0.7rem', backgroundColor: '#0d6efd', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>
                            {formatBookmakerName(bet.bookmaker)}
                          </span>
                        )}
                        {bet.odds && (
                          <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: bet.odds > 0 ? '#20c997' : '#17a2b8' }}>
                            {bet.odds > 0 ? `+${bet.odds}` : bet.odds}
                          </span>
                        )}
                      </div>
                      {/* Per-leg book icons */}
                      {buildResponse && buildResponse.legs && buildResponse.legs[originalIndex] && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '5px', paddingTop: '5px', borderTop: '1px solid #495057' }}>
                          {Object.entries(buildResponse.legs[originalIndex]).sort((a, b) => {
                            const aPrice = a[1].price ? parseInt(String(a[1].price).replace('+', '')) : -9999;
                            const bPrice = b[1].price ? parseInt(String(b[1].price).replace('+', '')) : -9999;
                            return bPrice - aPrice;
                          }).map(([bookId, bookData]) => {
                            const hasLink = !!bookData.dl;
                            const price = bookData.price;
                            return (
                              <div
                                key={bookId}
                                onClick={() => hasLink && window.open(bookData.dl, '_blank')}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  backgroundColor: hasLink ? '#1a2a1a' : 'transparent',
                                  border: hasLink ? '1px solid #2d5a2d' : '1px solid #444',
                                  cursor: hasLink ? 'pointer' : 'default',
                                  opacity: hasLink ? 1 : 0.35,
                                }}
                              >
                                <img
                                  src={`/logos/${bookId.toLowerCase()}.png`}
                                  alt={bookId}
                                  style={{ height: '14px', width: '14px', objectFit: 'contain', filter: hasLink ? 'none' : 'grayscale(100%)' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                                {price && (
                                  <span style={{ fontSize: '0.65rem', fontWeight: 600, color: String(price).startsWith('+') ? '#20c997' : '#17a2b8' }}>
                                    {price}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ));
            })()}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <span
                onClick={onClearAll}
                style={{ fontSize: '0.8rem', color: '#6c757d', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear All
              </span>
              <button
                onClick={() => setBuildTrigger(t => t + 1)}
                disabled={buildLoading}
                style={{
                  background: 'transparent',
                  border: '1px solid #495057',
                  borderRadius: '4px',
                  color: '#adb5bd',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  padding: '4px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Refresh
              </button>
              {buildResponse && buildResponse.betslip_id && (
                <button
                  onClick={async () => {
                    setSavingLink(true);
                    setSavedLink(false);
                    try {
                      const res = await fetch('/api/betslip/save', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ betslip_id: buildResponse.betslip_id })
                      });
                      if (res.ok) {
                        const data = await res.json();
                        const url = data.url || `${window.location.origin}/betslip/${buildResponse.betslip_id}`;
                        await navigator.clipboard.writeText(url);
                        setSavedLink(true);
                        setTimeout(() => setSavedLink(false), 2000);
                      }
                    } catch (err) {
                      console.error('Failed to save betslip:', err);
                    } finally {
                      setSavingLink(false);
                    }
                  }}
                  disabled={savingLink}
                  style={{
                    background: 'transparent',
                    border: '1px solid #495057',
                    borderRadius: '4px',
                    color: savedLink ? '#20c997' : '#adb5bd',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    padding: '4px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {savedLink ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                  )}
                  {savedLink ? 'Copied' : 'Share'}
                </button>
              )}
            </div>
            {/* Build response */}
            {buildLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '16px' }}>
                <Spinner animation="border" size="sm" variant="info" />
                <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>Finding best prices...</span>
              </div>
            )}
            {buildError && (
              <div style={{ marginTop: '12px', padding: '10px', backgroundColor: 'rgba(220,53,69,0.1)', border: '1px solid #dc3545', borderRadius: '6px', fontSize: '0.8rem', color: '#dc3545' }}>
                {buildError}
              </div>
            )}
            {buildResponse && !buildLoading && (buildResponse.data || buildResponse.parlay) && (
              <div style={{ marginTop: '12px' }}>
                {(() => {
                  const parlayData = buildResponse.parlay || buildResponse.data;
                  const books = Object.entries(parlayData).map(([name, info]) => ({
                    name,
                    originalId: info.original_id,
                    dl: info.dl,
                    price: info.price,
                    error: info.error,
                    missingEntries: info.missing_entries
                  }));
                  // Sort: books with price first (best/highest price first), then books without
                  const withPrice = books.filter(b => b.price).sort((a, b) => {
                    const aNum = parseInt(a.price.replace('+', ''));
                    const bNum = parseInt(b.price.replace('+', ''));
                    return bNum - aNum;
                  });
                  const withoutPrice = books.filter(b => !b.price);
                  const sorted = [...withPrice, ...withoutPrice];

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {sorted.map(book => {
                        const hasPrice = !!book.price;
                        const hasMissing = book.missingEntries && book.missingEntries.length > 0;
                        const isClickable = hasPrice && book.dl;
                        let bgColor = '#2a2a2a';
                        let borderColor = '#444';
                        let rowOpacity = 0.4;
                        if (hasPrice && !hasMissing) {
                          bgColor = '#1a2a1a'; borderColor = '#2d5a2d'; rowOpacity = 1;
                        } else if (hasPrice && hasMissing) {
                          bgColor = '#000000'; borderColor = '#333'; rowOpacity = 1;
                        }
                        return (
                          <div
                            key={book.originalId}
                            onClick={() => isClickable && window.open(book.dl, '_blank')}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '8px 10px',
                              backgroundColor: bgColor,
                              border: `1px solid ${borderColor}`,
                              borderRadius: '6px',
                              cursor: isClickable ? 'pointer' : 'default',
                              opacity: rowOpacity,
                              transition: 'background-color 0.15s'
                            }}
                          >
                            <img
                              src={`/logos/${book.originalId}.png`}
                              alt={book.name}
                              style={{ height: '24px', width: '24px', objectFit: 'contain', marginRight: '10px', filter: hasPrice ? 'none' : 'grayscale(100%)' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span style={{ flex: 1, fontSize: '0.85rem', color: hasPrice ? '#ccc' : '#666', fontWeight: 500 }}>
                              {book.name}
                            </span>
                            {hasPrice ? (
                              <span style={{
                                fontSize: '0.95rem',
                                fontWeight: 'bold',
                                color: book.price.startsWith('+') ? '#20c997' : '#17a2b8'
                              }}>
                                {book.price}
                              </span>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#666' }}>
                                {book.error ? book.error.substring(0, 30) : 'N/A'}
                              </span>
                            )}
                            {book.dl && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(book.dl).then(() => {
                                    setCopiedBookId(book.originalId);
                                    setTimeout(() => setCopiedBookId(null), 1500);
                                  });
                                }}
                                style={{
                                  marginLeft: '8px',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '2px'
                                }}
                                title="Copy link"
                              >
                                {copiedBookId === book.originalId ? (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#20c997" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                ) : (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                                  </svg>
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
        </>)}
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className={isDarkMode ? 'dark-mode' : ''}
    >
      <Modal.Header
        closeButton
        style={{
          borderBottom: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6',
          backgroundColor: isDarkMode ? '#212529' : '#fff',
          color: isDarkMode ? '#fff' : '#000'
        }}
      >
        <Modal.Title>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginRight: '10px', verticalAlign: 'middle' }}
          >
            <path
              d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M10 4v16M14 4v16"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          Betslip ({betslipItems.length})
          <span style={{ fontSize: '0.55rem', fontWeight: 700, color: '#fff', backgroundColor: '#0d6efd', padding: '1px 5px', borderRadius: '3px', marginLeft: '6px', verticalAlign: 'middle', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Beta</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          maxHeight: '60vh',
          overflowY: 'auto',
          backgroundColor: isDarkMode ? '#212529' : '#fff',
          color: isDarkMode ? '#fff' : '#000'
        }}
      >
        {renderTabToggle()}

        {activeTab === 'myslips' ? renderMySlips() : (<>
        {/* Submit Status Alert */}
        {submitStatus && (
          <Alert
            variant={submitStatus.type === 'error' ? 'danger' : submitStatus.type}
            dismissible
            onClose={() => setSubmitStatus(null)}
            style={{ marginBottom: '15px' }}
          >
            {submitStatus.message}
          </Alert>
        )}

        {betslipItems.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: isDarkMode ? '#adb5bd' : '#6c757d'
          }}>
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginBottom: '20px', opacity: 0.5 }}
            >
              <path
                d="M4 4h16v4.5c-1.1 0-2 .9-2 2s.9 2 2 2V20H4v-7.5c1.1 0 2-.9 2-2s-.9-2-2-2V4z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
            <h5>Your betslip is empty</h5>
            <p>Click on odds to add bets</p>
          </div>
        ) : (
          <ListGroup variant={isDarkMode ? 'flush' : 'flush'}>
            {betslipItems.map((bet, index) => (
              <ListGroup.Item
                key={bet.id}
                style={{
                  backgroundColor: isDarkMode ? '#343a40' : '#f8f9fa',
                  border: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6',
                  marginBottom: '10px',
                  borderRadius: '8px',
                  padding: '15px',
                  color: isDarkMode ? '#fff' : '#000'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    {/* Bet Description */}
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      {getTeamLogoUrl && bet.team && (
                        <img
                          src={getTeamLogoUrl(bet.team, bet.sport || 'nfl')}
                          alt={bet.team}
                          style={{
                            width: '24px',
                            height: '24px',
                            marginRight: '8px',
                            borderRadius: '50%'
                          }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <strong style={{ fontSize: '1.05rem' }}>{formatBetDescription(bet)}</strong>
                    </div>

                    {/* Game Info */}
                    <div style={{ fontSize: '0.85rem', color: isDarkMode ? '#868e96' : '#6c757d', marginBottom: '8px' }}>
                      {bet.awayTeam} @ {bet.homeTeam}
                    </div>

                    {/* Bookmaker and Odds */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {bet.bookmaker && (
                        <Badge bg="primary" style={{ fontSize: '0.85rem' }}>
                          {formatBookmakerName(bet.bookmaker)}
                        </Badge>
                      )}
                      {bet.odds && (
                        <Badge
                          bg={bet.odds.toString().startsWith('-') ? 'success' : 'info'}
                          style={{ fontSize: '0.9rem', fontWeight: 'bold' }}
                        >
                          {bet.odds > 0 ? `+${bet.odds}` : bet.odds}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onRemoveBet(bet.id)}
                    style={{
                      marginLeft: '10px',
                      minWidth: '32px',
                      height: '32px',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ×
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
        </>)}
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: isDarkMode ? '1px solid #495057' : '1px solid #dee2e6',
          backgroundColor: isDarkMode ? '#212529' : '#fff'
        }}
      >
        {betslipItems.length > 0 && (
          <>
            <Button variant="outline-secondary" onClick={onClearAll} disabled={isSubmitting}>
              Clear All
            </Button>
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Betslip'}
            </Button>
          </>
        )}
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BetslipModal;
